/*
  MDN suggested snippet to detect if local storage is BOTH supported and AVAILABLE

  This will be invoked prior to reading or writing. I added a property check so it short circuits
  the throw-on-purpose checking.
*/
function isStorageAvailable(type) {
  const storage = window[type];
  try {
    if (!storage) {
      return false;
    }
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage.length !== 0
    );
  }
}

function writeTo(storageObject, key, value) {
  // TODO: Replace this with an .isImmutable check once we upgade to 4.0
  if (value.toJS) {
    console.error(
      'You are trying to serialize an immutable object to local storage, this cannot be parsed'
    );
  }
  /*
    Wrapped in try/catch to ensure we don't horrendously bomb
    if our isStorageAvailable check passes, but we fail anyway - probably
    due to a storage limit being hit.

    // TODO: Possibly clear storage and try again? Diagnostic?
  */
  try {
    return storageObject.setItem(key, JSON.stringify(value));
  } catch (e) {
    // deliberately a no-op
    return undefined; // what a setItem call would return if successful.
  }
}

function readFrom(storageObject, key, defaultValue = undefined) {
  // TODO: perhaps try/catch this in the case something serializes very strangely.
  return storageObject.getItem(key) ? JSON.parse(storageObject.getItem(key)) : defaultValue;
}

const session = {
  get: (key, defaultValue) => {
    if (isStorageAvailable('sessionStorage')) {
      return readFrom(sessionStorage, key, defaultValue);
    }
    return defaultValue || undefined;
  },
  set: (key, value) => {
    if (isStorageAvailable('sessionStorage')) {
      return writeTo(sessionStorage, key, value);
    }
    return undefined;
  }
};

const local = {
  get: (key, defaultValue) => {
    if (isStorageAvailable('localStorage')) {
      return readFrom(localStorage, key, defaultValue);
    }
    return defaultValue || undefined;
  },
  set: (key, value) => {
    if (isStorageAvailable('localStorage')) {
      return writeTo(localStorage, key, value);
    }
    return undefined;
  }
};

/**
 * Utility for managing timed dismissals in localStorage.
 * Useful for modals, banners, or notifications that should reappear after a time period.
 *
 * @example
 * const dismissal = timedDismissal('myModal:dismissed');
 *
 * if (dismissal.shouldShow(14)) { // 14 days
 *   // Show modal
 * }
 *
 * // When user dismisses:
 * dismissal.dismiss();
 */
const timedDismissal = (storageKey) => ({
  /**
   * Check if enough time has passed since dismissal to show again
   * @param {number} days - Number of days before showing again
   * @returns {boolean} - True if should show, false if still within dismissal period
   */
  shouldShow: (days) => {
    if (!isStorageAvailable('localStorage')) {
      return true; // Default to showing if localStorage unavailable
    }

    try {
      const dismissedAt = localStorage.getItem(storageKey);

      if (!dismissedAt) {
        // Never dismissed before, should show
        return true;
      }

      const dismissedTimestamp = parseInt(dismissedAt, 10);
      const daysInMs = days * 24 * 60 * 60 * 1000;
      const now = Date.now();

      // Show if enough time has passed since dismissal
      return now - dismissedTimestamp >= daysInMs;
    } catch (error) {
      // If localStorage is not available or parsing fails, default to showing
      return true;
    }
  },

  /**
   * Mark as dismissed with current timestamp
   */
  dismiss: () => {
    if (!isStorageAvailable('localStorage')) {
      return;
    }

    try {
      localStorage.setItem(storageKey, Date.now().toString());
    } catch (error) {
      // Fail silently if localStorage is not available
    }
  }
});

export {local, session, timedDismissal};
